import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";

const faqItems = [
  {
    q: "Will this increase support tickets?",
    a: "The goal is the opposite. A clear flow plus consistent execution reduces first-hour friction and guest confusion.",
  },
  {
    q: "What lead time do you require?",
    a: "24 hours.",
  },
  {
    q: "Can we use this without integrations?",
    a: "Yes. Manual onboarding is available if needed.",
  },
  {
    q: "Can we scale to enterprise later?",
    a: "Yes. Bellhopt supports portfolios of any size, including enterprise portfolios of 1,000+ homes.",
  },
];

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">{title}</h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">{children}</div>
    </section>
  );
}

function ForMidMarketPropertyManagersPage() {
  const title = "Mid-Market Property Managers | Arrival-Ready Amenities That Scale";
  const description =
    "Deliver hotel-level convenience without adding operational burden. Bellhopt Concierge delivers, stocks, and organizes with a 24-hour lead time. Built to scale.";
  const canonical = "https://bellhopt.com/for/mid-market-property-managers/";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Premium Arrivals For Growing Property Management Teams",
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://bellhopt.com/" },
        { "@type": "ListItem", position: 2, name: "For Property Managers", item: "https://bellhopt.com/for/property-managers/" },
        { "@type": "ListItem", position: 3, name: "Mid-Market", item: canonical },
      ],
    },
  ];

  return (
    <div className="w-full min-h-screen bg-[#F9FAFB] pt-[72px] font-segoe">
      <SeoHead
        title={title}
        description={description}
        canonical="/for/mid-market-property-managers/"
        robots="index,follow"
        ogTitle="Mid-Market Portfolios, Elevated"
        ogDescription="A concierge layer that helps growing portfolios deliver a consistent arrival-ready standard."
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto py-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} p-8 mobile:p-5`}>
          <h1 className="font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight">
            Premium Arrivals For Growing Property Management Teams
          </h1>
          <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
            Mid-market operators win by building repeatable systems before growth adds complexity. Bellhopt provides a concierge layer that helps you deliver an arrival-ready standard consistently, without expanding your internal workload.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started With Bellhopt</a>
            <Link to="/how-it-works" className={SECONDARY_CTA}>How It Works</Link>
          </div>
          <p className="mt-4 text-[13px] text-[#6B7280]">
            24-hour lead time required. Built for multi-market execution.
          </p>
        </section>

        <div className="mt-6 space-y-6">
          <Section id="why-mid-market" title="Why This Matters In Mid-Market">
            <p>Guests compare listings locally. The operators who deliver a smoother first hour often win.</p>
            <p className="mt-4">At the same time, mid-market teams cannot absorb:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>more coordination</li>
              <li>more exceptions</li>
              <li>more guest messaging</li>
            </ul>
            <p className="mt-4">Bellhopt is designed to improve experience without adding chaos.</p>
          </Section>

          <Section id="what-you-get" title="What You Get">
            <ul className="list-disc pl-6">
              <li>Guest-friendly ordering flow</li>
              <li>Delivery, stocking, and basic organization by Bellhopt Concierge</li>
              <li>Repeatable execution that reduces back-and-forth</li>
              <li>Clear policies that protect quality</li>
            </ul>
          </Section>

          <Section id="rollout" title="Rollout In Weeks, Not Quarters">
            <ol className="list-decimal pl-6 space-y-4">
              <li>
                <strong>Onboard your properties</strong>
                <p>Integration or manual onboarding.</p>
              </li>
              <li>
                <strong>Standardize the arrival-ready playbook</strong>
                <p>Define what "arrival-ready" means for your brand.</p>
              </li>
              <li>
                <strong>Launch</strong>
                <p>Make it available across your portfolio and scale market by market.</p>
              </li>
            </ol>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Ready To Elevate Your Portfolio?
            </h2>
            <p className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Build a repeatable arrival-ready standard before growth adds complexity.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started</a>
              <a href="mailto:sales@bellhopt.com" className={SECONDARY_CTA}>Talk To Sales</a>
            </div>
          </section>

          <Section id="faq" title="FAQ">
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details key={item.q} className="rounded-2xl border border-[#E5E7EB] p-4 bg-white/90 open:bg-[#F9FAFB]">
                  <summary className="cursor-pointer font-segoe font-semibold text-[18px] text-[#111827]">{item.q}</summary>
                  <p className="mt-3 text-[16px] text-[#6B7280] leading-7">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">Related Pages</h2>
            <ul className="mt-4 list-disc pl-6 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              <li><Link className="text-primary" to="/for/property-managers">For property managers</Link></li>
              <li><Link className="text-primary" to="/for/enterprise-property-managers">Enterprise property managers</Link></li>
              <li><Link className="text-primary" to="/integrations">Integrations</Link></li>
              <li><Link className="text-primary" to="/pricing">Pricing</Link></li>
              <li><Link className="text-primary" to="/pre-arrival-groceries">Pre-arrival groceries</Link></li>
              <li><Link className="text-primary" to="/vacation-rental-grocery-delivery">Vacation rental grocery delivery</Link></li>
            </ul>
          </section>
        </div>
      </main>
    </div>
  );
}

export default ForMidMarketPropertyManagersPage;
